import Box from "@mui/material/Box"
import type { GroupStyleI, SimpleStyleT } from "tweenly"
import ColorField from "./ColorField"
import PrefixedInput from "./PrefixedInput"
import PropertyRow from "./PropertyRow"

interface Props {
    styleProperty: GroupStyleI
    handleOnChange: (e: any) => void
}

/**
 * Shadow Input Component
 * Renders structured inputs for drop shadow properties:
 * - Color (RGBA)
 * - Blur (px)
 * - Offset X (px)
 * - Offset Y (px)
 */
const ShadowInput = ({ styleProperty, handleOnChange }: Props) => {
    const shadowValue = styleProperty.value || {
        offsetX: 0,
        offsetY: 4,
        blur: 4,
        spread: 0,
        color: { r: 0, g: 0, b: 0, a: 0.25 },
    }

    // Handle color change
    const handleColorChange = (e: any) => {
        const newValue = {
            ...shadowValue,
            color: e.target.value,
        }
        handleOnChange({ target: { name: styleProperty.property, value: newValue } })
    }

    // Handle blur change
    const handleBlurChange = (e: any) => {
        const newValue = {
            ...shadowValue,
            blur: parseFloat(e.target.value) || 0,
        }
        handleOnChange({ target: { name: styleProperty.property, value: newValue } })
    }

    // Handle offsetX change
    const handleOffsetXChange = (e: any) => {
        const newValue = {
            ...shadowValue,
            offsetX: parseFloat(e.target.value) || 0,
        }
        handleOnChange({ target: { name: styleProperty.property, value: newValue } })
    }

    // Handle offsetY change
    const handleOffsetYChange = (e: any) => {
        const newValue = {
            ...shadowValue,
            offsetY: parseFloat(e.target.value) || 0,
        }
        handleOnChange({ target: { name: styleProperty.property, value: newValue } })
    }

    // Create temporary style properties for each sub-property
    const colorProperty: GroupStyleI = {
        ...styleProperty,
        property: "color",
        value: shadowValue.color,
        input: {
            type: "color",
            props: "",
            label: "color",
        },
    }

    const blurProperty: GroupStyleI = {
        ...styleProperty,
        property: "blur",
        value: shadowValue.blur,
        unit: "px",
        input: {
            type: "number",
            props: {
                inputProps: {
                    min: 0,
                    max: 100,
                    step: 1,
                },
                label: "Blur",
            },
        },
    }

    const offsetXProperty: GroupStyleI = {
        ...styleProperty,
        property: "offsetX",
        value: shadowValue.offsetX,
        unit: "px",
        input: {
            type: "number",
            props: {
                inputProps: {
                    min: -100,
                    max: 100,
                    step: 1,
                },
                label: "X",
            },
        },
    }

    const offsetYProperty: GroupStyleI = {
        ...styleProperty,
        property: "offsetY",
        value: shadowValue.offsetY,
        unit: "px",
        input: {
            type: "number",
            props: {
                inputProps: {
                    min: -100,
                    max: 100,
                    step: 1,
                },
                label: "Y",
            },
        },
    }

    return (
        // the same rows as every other section: the color on its own, then two to a row
        <>
            <PropertyRow>
                <ColorField
                    styleProperty={colorProperty as unknown as SimpleStyleT}
                    handleOnChange={handleColorChange}
                />
            </PropertyRow>
            <PropertyRow>
                <Box display="flex" gap={1}>
                    <Box flex={1} minWidth={0}>
                        <PrefixedInput
                            name="blur"
                            value={blurProperty.value}
                            label="blur (px)"
                            tooltip="Shadow blur (px)"
                            handleOnChange={handleBlurChange}
                            min={0}
                            max={100}
                        />
                    </Box>
                    <Box flex={1} />
                </Box>
            </PropertyRow>
            <PropertyRow>
                <Box display="flex" gap={1}>
                    <Box flex={1} minWidth={0}>
                        <PrefixedInput
                            name="offsetX"
                            value={offsetXProperty.value}
                            label="offset X (px)"
                            tooltip="Shadow offset X (px)"
                            handleOnChange={handleOffsetXChange}
                            min={-100}
                            max={100}
                        />
                    </Box>
                    <Box flex={1} minWidth={0}>
                        <PrefixedInput
                            name="offsetY"
                            value={offsetYProperty.value}
                            label="offset Y (px)"
                            tooltip="Shadow offset Y (px)"
                            handleOnChange={handleOffsetYChange}
                            min={-100}
                            max={100}
                        />
                    </Box>
                </Box>
            </PropertyRow>
        </>
    )
}

export default ShadowInput
